/**
 * @fileoverview Job audit log panel (MOR-1012).
 *
 * Full change history of a job across every agent, user, and system write —
 * job settings, entity records (the same entries the per-record History
 * popovers show), and task activity — in one filterable table with a CSV
 * export.
 *
 * Loads in two steps so large jobs stay responsive: the index (every entry
 * minus its before/after snapshots) comes from the `getJobAuditIndex`
 * action on mount / Refresh; the snapshots for the visible page come from
 * the `getAuditEntryDetails` query as the user pages. CSV export resolves
 * the filtered rows' snapshots in batches before downloading.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAction, useConvex } from "convex/react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { FIELD_LABELS, humanizeFieldKey } from "@cvx/lib/fieldLabels";
import {
  ArrowRight,
  Bot,
  ChevronLeft,
  ChevronRight,
  Database,
  Download,
  Loader2,
  RefreshCw,
  ScrollText,
  Search,
  User as UserIcon,
} from "lucide-react";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { toast } from "sonner";
import { AuditQueryBuilder } from "./-ui.job-audit-query-builder";
import {
  AUDIT_FILTER_FIELD_LABELS,
  DEFAULT_AUDIT_QUERY,
  conditionMatches,
  periodStart,
  type AuditColumn,
  type AuditDimension,
  type AuditFilterField,
  type AuditQuery,
} from "./-ui.job-audit-query";

type AuditIndexResult = Awaited<
  ReturnType<
    ReturnType<typeof useAction<typeof api.jobs.auditLog.getJobAuditIndex>>
  >
>;
type AuditIndexEntry = AuditIndexResult["entries"][number];
type Snapshot = Record<string, unknown> | undefined;
type AuditDetails = { before?: Snapshot; after?: Snapshot };
type AuditEntry = AuditIndexEntry & AuditDetails;
type ActorType = AuditEntry["actorType"];

const PAGE_SIZE = 50;
/** Ids per details request when resolving snapshots for the CSV export. */
const EXPORT_BATCH = 200;

const ACTOR_META: Record<
  ActorType,
  { label: string; color: BadgeColor; icon: typeof Bot }
> = {
  user: { label: "User", color: "emerald", icon: UserIcon },
  agent: { label: "Agent", color: "blue", icon: Bot },
  system: { label: "System", color: "sky", icon: Database },
};

// ---------------------------------------------------------------------------
// Value formatting (shared by table cells and the CSV export)
// ---------------------------------------------------------------------------

function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? humanizeFieldKey(key);
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    const json = JSON.stringify(value);
    return json.length > 200 ? `${json.slice(0, 200)}…` : json;
  } catch {
    return String(value);
  }
}

/** Table cells show a clipped value; the CSV keeps the full text. */
function clip(value: string, max = 240): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function snapshotLines(
  snapshot: Record<string, unknown> | undefined,
): Array<{ key: string; label: string; value: string }> {
  if (!snapshot) return [];
  return Object.entries(snapshot).map(([key, value]) => ({
    key,
    label: key === "value" ? "Value" : fieldLabel(key),
    value: formatValue(value),
  }));
}

function snapshotToText(snapshot: Record<string, unknown> | undefined): string {
  return snapshotLines(snapshot)
    .map((l) => `${l.label}: ${l.value}`)
    .join("; ");
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

/**
 * A cell starting with any of these is read as a formula by Excel/Sheets
 * (OWASP CSV injection guidance) — `=`, `+`, `-`, `@`, tab, or CR.
 */
const CSV_FORMULA_PREFIX = /^[=+\-@\t\r]/;

function escapeCsvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  let s = String(value);
  // Neutralise formula injection: prefix a leading apostrophe so the cell
  // is read as literal text. The original value round-trips by stripping
  // this one known leading character.
  if (CSV_FORMULA_PREFIX.test(s)) {
    s = `'${s}`;
  }
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function timestampSlug(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function downloadCsv(csvBody: string, filename: string): void {
  // BOM so Excel renders non-ASCII (creditor names, dashes) correctly.
  const csv = "\uFEFF" + csvBody;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * The value each filter field reads from an index row. Pure and stateless —
 * kept at module scope rather than redefined on every render.
 */
function fieldValue(entry: AuditIndexEntry, field: AuditFilterField) {
  switch (field) {
    case "location":
      return entry.location;
    case "actorType":
      return entry.actorType;
    case "actorName":
      return entry.actorName;
    case "action":
      return entry.action;
    case "recordLabel":
      return entry.recordLabel;
  }
}

function exportAuditLogToCsv(jobName: string, entries: AuditEntry[]): void {
  const headers = [
    "Job",
    "Change Location",
    "User / Agent",
    "Date",
    "Description",
    "Before",
    "After",
  ];
  const lines = [headers.map(escapeCsvCell).join(",")];
  for (const entry of entries) {
    const location = entry.recordLabel
      ? `${entry.location} — ${entry.recordLabel}`
      : entry.location;
    lines.push(
      [
        jobName,
        location,
        `${ACTOR_META[entry.actorType].label}: ${entry.actorName}`,
        formatTimestamp(entry.timestamp),
        entry.description,
        snapshotToText(entry.before),
        snapshotToText(entry.after),
      ]
        .map(escapeCsvCell)
        .join(","),
    );
  }
  downloadCsv(lines.join("\r\n"), `audit-log-${timestampSlug()}.csv`);
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export function JobAuditLogPanel({ jobId }: { jobId: Id<"jobs"> }) {
  const getIndex = useAction(api.jobs.auditLog.getJobAuditIndex);
  const convex = useConvex();
  const [data, setData] = useState<AuditIndexResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<AuditQuery>(DEFAULT_AUDIT_QUERY);
  const [page, setPage] = useState(0);
  const patchQuery = useCallback(
    (patch: Partial<AuditQuery>) => setQuery((q) => ({ ...q, ...patch })),
    [],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getIndex({ jobId });
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load audit log");
    } finally {
      setIsLoading(false);
    }
  }, [getIndex, jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Filter dimensions and their options, from what the job actually has.
  const dimensions = useMemo<AuditDimension[]>(() => {
    if (!data) return [];
    const fields: AuditFilterField[] = [
      "location",
      "actorType",
      "actorName",
      "action",
      "recordLabel",
    ];
    return fields.map((field) => {
      const values = new Set<string>();
      for (const entry of data.entries) {
        const value = fieldValue(entry, field);
        if (value) values.add(value);
      }
      const options = [...values].sort().map((value) => ({
        value,
        label:
          field === "actorType"
            ? (ACTOR_META[value as ActorType]?.label ?? value)
            : field === "action"
              ? value.replace(/_/g, " ")
              : value,
      }));
      return { field, label: AUDIT_FILTER_FIELD_LABELS[field], options };
    });
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    const since = periodStart(query.period);
    return data.entries.filter((entry) => {
      if (since !== null && entry.timestamp < since) return false;
      for (const condition of query.conditions) {
        if (!conditionMatches(condition, fieldValue(entry, condition.field))) {
          return false;
        }
      }
      if (!needle) return true;
      const haystack = [
        entry.location,
        entry.recordLabel ?? "",
        entry.actorName,
        entry.description,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [data, search, query]);

  // Any filter change goes back to the first page.
  useEffect(() => {
    setPage(0);
  }, [search, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageEntries = useMemo(
    () => filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE),
    [filtered, safePage],
  );
  const pageIds = useMemo(() => pageEntries.map((e) => e.id), [pageEntries]);

  // Snapshots for the visible page only.
  const { data: details } = useQuery({
    ...convexQuery(api.jobs.auditLog.getAuditEntryDetails, {
      jobId,
      ids: pageIds,
    }),
    enabled: pageIds.length > 0,
  });
  const detailsById = useMemo(() => {
    const map = new Map<string, AuditDetails>();
    for (const d of details ?? []) map.set(d.id, d);
    return map;
  }, [details]);

  const handleExport = async () => {
    if (!data || filtered.length === 0) {
      toast.info("Nothing to export");
      return;
    }
    setIsExporting(true);
    try {
      const snapshots = new Map<string, AuditDetails>();
      for (let i = 0; i < filtered.length; i += EXPORT_BATCH) {
        const batch = await convex.query(
          api.jobs.auditLog.getAuditEntryDetails,
          {
            jobId,
            ids: filtered.slice(i, i + EXPORT_BATCH).map((e) => e.id),
          },
        );
        for (const d of batch) snapshots.set(d.id, d);
      }
      exportAuditLogToCsv(
        data.jobName,
        filtered.map((e) => ({ ...e, ...snapshots.get(e.id) })),
      );
      toast.success(
        `Exported ${filtered.length} change${filtered.length === 1 ? "" : "s"} to CSV`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading && !data) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Try again
        </Button>
      </div>
    );
  }

  if (!data) return null;

  const show = (column: AuditColumn) => query.columns.includes(column);
  const firstRow = filtered.length === 0 ? 0 : safePage * PAGE_SIZE + 1;
  const lastRow = Math.min((safePage + 1) * PAGE_SIZE, filtered.length);

  return (
    <div className="flex flex-col gap-4">
      {/* Header + actions */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <ScrollText className="h-5 w-5" />
            Audit log
          </h2>
          <p className="text-sm text-muted-foreground">
            Every recorded change on this job — agents, users and system · as of{" "}
            {new Date(data.computedAt).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load()}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleExport()}
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export CSV
          </Button>
        </div>
      </div>

      {/* Query builder */}
      <AuditQueryBuilder
        query={query}
        onChange={patchQuery}
        dimensions={dimensions}
      />

      {/* Search + count */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search changes..."
            className="h-9 w-64 pl-8 text-sm"
          />
        </div>
        <span className="ml-auto text-xs text-muted-foreground">
          {filtered.length === data.entries.length
            ? `${data.entries.length} change${data.entries.length === 1 ? "" : "s"}`
            : `${filtered.length} of ${data.entries.length} changes`}
          {data.truncated ? " · oldest entries truncated" : ""}
        </span>
      </div>

      {/* Table — scrolls inside its own container so the header stays pinned */}
      {filtered.length === 0 ? (
        <p className="rounded-md border py-10 text-center text-sm text-muted-foreground">
          No changes match the current filters.
        </p>
      ) : (
        <div className="max-h-[calc(100vh-21rem)] overflow-auto rounded-md border">
          <table className="w-full min-w-[1100px] table-fixed text-sm">
            <thead className="sticky top-0 z-10 bg-background">
              <tr className="border-b text-left text-xs text-muted-foreground">
                {show("date") && (
                  <th className="w-40 whitespace-nowrap bg-muted/40 px-3 py-2 font-medium">
                    Date
                  </th>
                )}
                {show("location") && (
                  <th className="w-52 bg-muted/40 px-3 py-2 font-medium">
                    Change location
                  </th>
                )}
                {show("actor") && (
                  <th className="w-40 bg-muted/40 px-3 py-2 font-medium">
                    User / Agent
                  </th>
                )}
                {show("description") && (
                  <th className="w-[28%] bg-muted/40 px-3 py-2 font-medium">
                    Description
                  </th>
                )}
                {show("change") && (
                  <th className="bg-muted/40 px-3 py-2 font-medium">Change</th>
                )}
              </tr>
            </thead>
            <tbody>
              {pageEntries.map((entry) => (
                <AuditRow
                  key={entry.id}
                  entry={{ ...entry, ...detailsById.get(entry.id) }}
                  detailsLoaded={details !== undefined}
                  columns={query.columns}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Showing {firstRow}–{lastRow} of {filtered.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span>
              Page {safePage + 1} of {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={safePage >= pageCount - 1}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function AuditRow({
  entry,
  detailsLoaded,
  columns,
}: {
  entry: AuditEntry;
  detailsLoaded: boolean;
  columns: AuditColumn[];
}) {
  const show = (column: AuditColumn) => columns.includes(column);
  const meta = ACTOR_META[entry.actorType];
  const Icon = meta.icon;
  const before = snapshotLines(entry.before);
  const afterLines = snapshotLines(entry.after);
  const beforeByKey = new Map(before.map((l) => [l.key, l]));
  // One row per changed field: after-side drives; before-only fields follow.
  const diffKeys = [
    ...new Set([...afterLines.map((l) => l.key), ...before.map((l) => l.key)]),
  ];

  return (
    <tr className="border-b align-top last:border-0">
      {show("date") && (
        <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
          {formatTimestamp(entry.timestamp)}
        </td>
      )}
      {show("location") && (
        <td className="px-3 py-2">
          <div className="font-medium">{entry.location}</div>
          {entry.recordLabel && (
            <div
              className="truncate text-xs text-muted-foreground"
              title={entry.recordLabel}
            >
              {entry.recordLabel}
            </div>
          )}
        </td>
      )}
      {show("actor") && (
        <td className="whitespace-nowrap px-3 py-2">
          <Badge color={meta.color} variant="soft" size="sm" className="gap-1">
            <Icon className="h-3 w-3" />
            {meta.label}
          </Badge>
          {entry.actorType === "user" && (
            <div className="mt-0.5 text-xs text-muted-foreground">
              {entry.actorName}
            </div>
          )}
          {entry.actorType !== "user" && entry.actorName !== meta.label && (
            <div className="mt-0.5 text-xs text-muted-foreground">
              {entry.actorName}
            </div>
          )}
        </td>
      )}
      {show("description") && (
        <td className="break-words px-3 py-2">{entry.description}</td>
      )}
      {show("change") && (
        <td className="break-words px-3 py-2">
          {diffKeys.length === 0 ? (
            <span className="text-muted-foreground">
              {detailsLoaded ? "—" : "…"}
            </span>
          ) : (
            <div className="flex flex-col gap-1">
              {diffKeys.map((key) => {
                const b = beforeByKey.get(key);
                const a = afterLines.find((l) => l.key === key);
                return (
                  <div
                    key={key}
                    className="flex flex-wrap items-center gap-1.5 text-xs"
                  >
                    <span className="font-medium uppercase tracking-wide text-muted-foreground">
                      {(a ?? b)?.label}
                    </span>
                    {b && (
                      <span className="rounded bg-red-50 px-1 py-0.5 text-red-700 line-through dark:bg-red-950/40 dark:text-red-400">
                        {clip(b.value)}
                      </span>
                    )}
                    {b && a && (
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    )}
                    {a && (
                      <span className="rounded bg-emerald-50 px-1 py-0.5 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                        {clip(a.value)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </td>
      )}
    </tr>
  );
}
